import { type PropsWithChildren, createContext, useContext } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useAccessToken } from 'reservations/account/providers/AccessTokenProvider';
import { useBookingConfig } from 'reservations/account/providers/ConfigProvider';
import { useFirstAvailableDateQuery } from 'reservations/availabilities/queries/useFirstAvailableDateQuery';
import { useDefaultPartySize } from 'reservations/booking/hooks/useDefaultPartySize';
import { useWaitlist } from 'reservations/waitlist/providers/WaitlistProvider';

interface FirstAvailableDateContextInterface {
  firstAvailability: Date | null;
  hasFirstAvailableDate: boolean;
}

const FirstAvailableDateContext =
  createContext<FirstAvailableDateContextInterface | null>(null);

export const FirstAvailableDateProvider = ({ children }: PropsWithChildren) => {
  const { maxAdvanceDays } = useBookingConfig();
  const partySize = useDefaultPartySize();
  const { slug, access_token: apiKey } = useAccessToken();
  const { hasActiveOnlineWaitlist } = useWaitlist();
  const { setValue } = useFormContext();
  const currentDate = useWatch({ name: 'date' });

  const { data, isPending } = useFirstAvailableDateQuery({
    slug,
    apiKey,
    partySize,
    maxAdvanceDays,
    handleSuccess: (date: Date | null) => {
      // only set the date if its greater than the current selected date
      // because this means that the first available date is ahead of the
      // current selected date
      if (date !== null && currentDate < date) {
        setValue('date', date);
      }
    }
  });

  if (hasActiveOnlineWaitlist) {
    return (
      <FirstAvailableDateContext.Provider
        value={{
          firstAvailability: null,
          hasFirstAvailableDate: false
        }}
      >
        {children}
      </FirstAvailableDateContext.Provider>
    );
  }

  if (isPending) {
    return <></>;
  }

  return (
    <FirstAvailableDateContext.Provider
      value={{
        firstAvailability: data ?? null,
        hasFirstAvailableDate: !!data
      }}
    >
      {children}
    </FirstAvailableDateContext.Provider>
  );
};

export function useFirstAvailableDate() {
  const context = useContext(FirstAvailableDateContext);
  if (context == null) {
    throw new Error(
      'useFirstAvailableDate must be used within FirstAvailableDateProvider'
    );
  }

  return context;
}
